import React, { useEffect, useState } from "react";
import api from "../api/api";
import toast from "react-hot-toast";
import { Calendar, Clock, Plus, X, CheckCircle2, XCircle, Hourglass } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

type Req = {
  id: string;
  type: "LEAVE" | "SHORT_LEAVE" | "OVERTIME_BEFORE" | "OVERTIME_AFTER";
  leaveKind?: "ANNUAL" | "CASUAL" | null;
  startDate: string;
  endDate: string | null;
  days: number | null;
  hours: number | null;
  reason: string;
  status: "PENDING" | "APPROVED" | "REJECTED";
  decidedAt: string | null;
  decisionNote: string | null;
  createdAt: string;
  manager?: { name: string } | null;
};

type Balance = { annual: { remaining: number; pending: number; available: number }; casual: { remaining: number; pending: number; available: number } };

const TYPE_LABEL: Record<string, string> = {
  LEAVE: "🏖️ Leave",
  SHORT_LEAVE: "🕐 Short Leave (2h)",
  OVERTIME_BEFORE: "⏰ Overtime (pre-approval)",
  OVERTIME_AFTER: "⌛ Overtime (already worked)",
};

function typeLabel(r: { type: string; leaveKind?: string | null }): string {
  if (r.type === "LEAVE") return r.leaveKind === "CASUAL" ? "🏝️ Casual Leave" : "🏖️ Annual Leave";
  return TYPE_LABEL[r.type];
}

const STATUS_META: Record<string, { icon: any; color: string; bg: string; label: string }> = {
  PENDING:  { icon: Hourglass,   color: "text-amber-700",  bg: "bg-amber-50",  label: "Pending" },
  APPROVED: { icon: CheckCircle2, color: "text-green-700", bg: "bg-green-50", label: "Approved" },
  REJECTED: { icon: XCircle,     color: "text-red-700",   bg: "bg-red-50",   label: "Rejected" },
};

export default function MyRequests() {
  const { user } = useAuth();
  const [rows, setRows] = useState<Req[]>([]);
  const [loading, setLoading] = useState(true);
  const [bal, setBal] = useState<Balance | null>(null);
  const [showForm, setShowForm] = useState(false);

  const [type, setType] = useState<"LEAVE" | "SHORT_LEAVE" | "OVERTIME_BEFORE" | "OVERTIME_AFTER">("LEAVE");
  const [leaveKind, setLeaveKind] = useState<"ANNUAL" | "CASUAL">("ANNUAL");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [hours, setHours] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api.get<Req[]>("/requests/my")
      .then(r => setRows(r.data))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
    api.get<Balance>("/requests/my-balance")
      .then(r => setBal(r.data))
      .catch(() => setBal(null));
  };

  useEffect(() => { load(); }, [user?.id]);

  const submit = async () => {
    if (!startDate) return toast.error("Start date required");
    if (type === "LEAVE" && !endDate) return toast.error("End date required for leave");
    if ((type === "OVERTIME_BEFORE" || type === "OVERTIME_AFTER") && (!hours || Number(hours) <= 0)) return toast.error("Hours required (>0)");

    setSaving(true);
    try {
      await api.post("/requests", {
        type,
        startDate,
        endDate: type === "LEAVE" ? endDate : undefined,
        hours: (type === "OVERTIME_BEFORE" || type === "OVERTIME_AFTER") ? Number(hours) : undefined,
        reason: reason.trim() || undefined,
        leaveKind: type === "LEAVE" ? leaveKind : undefined,
      });
      toast.success("Request submitted");
      setShowForm(false);
      setStartDate(""); setEndDate(""); setHours(""); setReason(""); setType("LEAVE"); setLeaveKind("ANNUAL");
      load();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto p-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3 min-w-0">
          <Calendar className="text-blue-600 shrink-0" size={26}/>
          <div className="min-w-0">
            <h1 className="text-lg sm:text-xl font-semibold text-gray-900">My Requests</h1>
            <p className="text-xs sm:text-sm text-gray-500">Leave & Overtime requests</p>
          </div>
        </div>
        <div className="flex items-center gap-3 ml-auto">
          {bal && (
            <div className="flex gap-2">
              <div className="text-right px-2 py-1 bg-blue-50 rounded-md">
                <div className="text-[10px] text-blue-600 uppercase tracking-wide">Annual</div>
                <div className={`text-sm sm:text-base font-bold ${bal.annual.available > 3 ? "text-blue-700" : bal.annual.available > 0 ? "text-amber-600" : "text-red-600"}`}>
                  {bal.annual.available.toFixed(1)}<span className="text-xs opacity-60">/{bal.annual.remaining.toFixed(0)}</span>
                </div>
              </div>
              <div className="text-right px-2 py-1 bg-purple-50 rounded-md">
                <div className="text-[10px] text-purple-600 uppercase tracking-wide">Casual</div>
                <div className={`text-sm sm:text-base font-bold ${bal.casual.available > 1 ? "text-purple-700" : bal.casual.available > 0 ? "text-amber-600" : "text-red-600"}`}>
                  {bal.casual.available.toFixed(1)}<span className="text-xs opacity-60">/{bal.casual.remaining.toFixed(0)}</span>
                </div>
              </div>
            </div>
          )}
          <button onClick={() => setShowForm(true)}
            className="flex items-center gap-1.5 bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 text-sm">
            <Plus size={16}/> <span className="hidden sm:inline">New Request</span><span className="sm:hidden">New</span>
          </button>
        </div>
      </div>

      {loading ? (
        <div className="p-6 text-gray-500">Loading...</div>
      ) : rows.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-xl p-8 text-center text-gray-400">
          No requests yet. Click "New Request" to submit one.
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Type</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Date(s)</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Amount</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Reason</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Status</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Manager Note</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map(r => {
                const meta = STATUS_META[r.status];
                const Icon = meta.icon;
                return (
                  <tr key={r.id}>
                    <td className="px-4 py-3 whitespace-nowrap">{typeLabel(r)}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-700">
                      {new Date(r.startDate).toLocaleDateString()}
                      {r.endDate && r.endDate !== r.startDate && ` → ${new Date(r.endDate).toLocaleDateString()}`}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap font-medium text-gray-800">
                      {r.days ? `${r.days} day${r.days === 1 ? "" : "s"}` : r.hours ? `${r.hours} hr${r.hours === 1 ? "" : "s"}` : "—"}
                    </td>
                    <td className="px-4 py-3 max-w-xs truncate text-gray-600" title={r.reason}>{r.reason || <span className="text-gray-300">—</span>}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium ${meta.bg} ${meta.color}`}>
                        <Icon size={12}/> {meta.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500 max-w-xs">
                      {r.decisionNote || (r.status !== "PENDING" && r.manager?.name ? `— ${r.manager.name}` : "")}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="px-5 py-4 border-b bg-blue-50 flex items-center justify-between">
              <div className="text-lg font-semibold text-blue-800">New Request</div>
              <button onClick={() => setShowForm(false)} className="text-gray-500 hover:text-gray-800"><X size={20}/></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select value={type} onChange={e => setType(e.target.value as any)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                  <option value="LEAVE">🏖️ Leave</option>
                  <option value="SHORT_LEAVE">🕐 Short Leave — 2 hours permission (إذن ساعتين)</option>
                  <option value="OVERTIME_BEFORE">⏰ Overtime — pre-approval (before working)</option>
                  <option value="OVERTIME_AFTER">⌛ Overtime — already worked (documentation)</option>
                </select>
              </div>
              <div className={type === "SHORT_LEAVE" ? "" : "grid grid-cols-2 gap-3"}>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {type === "LEAVE" ? "Start Date" : "Date"}
                  </label>
                  <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"/>
                </div>
                {type === "LEAVE" && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                    <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} min={startDate}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"/>
                  </div>
                )}
                {(type === "OVERTIME_BEFORE" || type === "OVERTIME_AFTER") && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Hours</label>
                    <input type="number" min="0.5" max="24" step="0.5" value={hours} onChange={e => setHours(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"/>
                  </div>
                )}
              </div>
              {type === "SHORT_LEAVE" && (
                <div className="text-xs text-blue-700 bg-blue-50 border border-blue-100 rounded-md p-2 -mt-2">
                  ⏱️ Fixed 2 hours permission — does not deduct from your leave balance.
                </div>
              )}
              {type === "LEAVE" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Leave Kind</label>
                  <div className="grid grid-cols-2 gap-2">
                    <button type="button" onClick={() => setLeaveKind("ANNUAL")}
                      className={`px-3 py-2 rounded-lg border text-sm transition ${
                        leaveKind === "ANNUAL" ? "bg-blue-50 border-blue-500 text-blue-800 font-semibold" : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"
                      }`}>
                      🏖️ Annual
                      {bal && <div className="text-[11px] font-normal opacity-70 mt-0.5">Available: {bal.annual.available.toFixed(1)}d</div>}
                    </button>
                    <button type="button" onClick={() => setLeaveKind("CASUAL")}
                      className={`px-3 py-2 rounded-lg border text-sm transition ${
                        leaveKind === "CASUAL" ? "bg-purple-50 border-purple-500 text-purple-800 font-semibold" : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"
                      }`}>
                      🏝️ Casual
                      {bal && <div className="text-[11px] font-normal opacity-70 mt-0.5">Available: {bal.casual.available.toFixed(1)}d</div>}
                    </button>
                  </div>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reason <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <textarea value={reason} onChange={e => setReason(e.target.value)} rows={3}
                  placeholder="Brief reason (optional)..."
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"/>
              </div>
              <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
                <button onClick={() => setShowForm(false)} className="px-4 py-2 rounded-lg text-gray-700 hover:bg-gray-100 text-sm">Cancel</button>
                <button onClick={submit} disabled={saving}
                  className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 text-sm disabled:opacity-50">
                  {saving ? "Submitting..." : "Submit"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
